/**
 * InternalEditEntry — wider Dialog replacing the attorney edit dialog in the internal
 * dashboard's row-click flow.
 *
 * Pure composition inside a shadcn Dialog: a 35/65 split DialogContent with
 * EditEntryBody as the left column and MicroTransactionsPanel on the right.
 * The Dialog provides the overlay, panel chrome, single top-right close button,
 * and Escape/backdrop dismissal.
 *
 * Public API is a drop-in replacement for the attorney edit dialog with two added props
 * for internal context (attorneyProfId, attorneyName). The shell owns NO
 * domain state of its own — each child panel manages itself.
 */

import {
	type Activity,
	EditEntryBody,
	type Matter,
	type SelectedRowData,
	type Task,
} from "@/features/attorney/components/v2/EditEntryBody";
import type { Abbreviation } from "@/shared/components/NarrativeTextArea/NarrativeTextArea";
import {
	Dialog,
	DialogContent,
	DialogTitle,
} from "@/shared/components/ui/dialog";

import { MicroTransactionsPanel } from "./MicroTransactionsPanel";

// Re-export so existing imports of these types from the internal feature
// folder continue to resolve without indirection.
export type { Activity, Matter, SelectedRowData, Task };

type InternalEditEntryProps = {
	onSave: (
		entry: {
			id: number;
			date: string;
			matter: string | null;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
		},
		shouldExport: boolean,
	) => void;
	onDuplicate?: (
		entry: {
			date: string;
			matter: string | null;
			matter_id: string;
			description: string;
			duration: number;
			task: string;
			activity: string;
			document_name: string | null;
			client: string | null;
			units_seconds: number;
			start_time: string | null;
			end_time: string | null;
		},
		originalId: number,
	) => Promise<void>;
	matters: Matter[];
	tasks: Task[];
	activities: Activity[];
	selectedRowData: SelectedRowData;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	abbreviations?: Abbreviation[];
	attorneyFeedback?: string | null;
	onResolveFeedback?: () => Promise<void>;
	exportError?: string | null;
};

export function InternalEditEntry({
	onSave,
	onDuplicate,
	matters,
	tasks,
	activities,
	selectedRowData,
	open,
	onOpenChange,
	abbreviations,
	attorneyFeedback,
	onResolveFeedback,
	exportError,
}: InternalEditEntryProps) {
	const close = () => onOpenChange(false);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				className={[
					"flex gap-0 p-0 overflow-hidden",
					"w-[95vw] max-w-[95vw] h-[92vh] max-h-[92vh] min-h-[640px]",
					"rounded-xl",
					"shadow-[0_32px_64px_-16px_rgba(0,0,0,0.22),0_12px_24px_-10px_rgba(0,0,0,0.12)]",
				].join(" ")}
			>
				<DialogTitle className="sr-only">Edit entry</DialogTitle>

				{/* Left column: edit form (35%) */}
				<EditEntryBody
					onSave={onSave}
					onDuplicate={onDuplicate}
					matters={matters}
					tasks={tasks}
					activities={activities}
					selectedRowData={selectedRowData}
					abbreviations={abbreviations}
					attorneyFeedback={attorneyFeedback}
					onResolveFeedback={onResolveFeedback}
					exportError={exportError}
					onClose={close}
					className="basis-[35%] min-w-0 overflow-y-auto border-r p-6"
				/>

				{/* Right column: micro-transactions (full height) */}
				<aside className="flex-1 min-w-0 flex flex-col bg-muted/30">
					<MicroTransactionsPanel transactionId={String(selectedRowData.id)} />
				</aside>
			</DialogContent>
		</Dialog>
	);
}
